import logger from '../utils/logger-rum';
import { HttpRequest } from '../stores/types/types';
import { buildSyncUrl, getAuthenticatedRequestInit, getSyncHeaders } from './utils';
import { Entity } from '../types/interfaces';
import type {
	OnsiteLocation,
	PreviousLocation,
	SrtStream,
} from '../stores/types/video';

export const GetSrtStreamsForMeeting = async (
	token: string,
	meetingId: number,
): Promise<SrtStream[]> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: [];
		}

		logger.error(`GetSrtStreamsForMeeting error: ${response.status}`);
	} catch (error) {
		logger.error(`GetSrtStreamsForMeeting error: ${error}`);
	}

	return [];
};

export const CreateSrtStream = async (
	token: string,
	meetingId: number,
	name: string,
	safetyImage: string,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					name,
					safetyImage,
				}),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`CreateSrtStream error: ${response.status}`);
	} catch (error) {
		logger.error(`CreateSrtStream error: ${error}`);
	}

	return null;
};

export const UpdateSrtStream = async (
	token: string,
	meetingId: number,
	streamId: number,
	updatedProps: Partial<SrtStream>,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}/${streamId}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify(updatedProps),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`UpdateSrtStream error: ${response.status}`);
	} catch (error) {
		logger.error(`UpdateSrtStream error: ${error}`);
	}

	return null;
};

export const DeleteSrtStream = async (
	token: string,
	meetingId: number,
	streamId: number,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}/${streamId}`),
			{
				method: HttpRequest.Delete,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`DeleteSrtStream error: ${response.status}`);
	} catch (error) {
		logger.error(`DeleteSrtStream error: ${error}`);
	}

	return null;
};

export const GetOnsiteLocationsForMeeting = async (
	token: string,
	meetingId: number,
): Promise<OnsiteLocation[]> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: [];
		}

		logger.error(`GetOnsiteLocationsForMeeting error: ${response.status}`);
	} catch (error) {
		logger.error(`GetOnsiteLocationsForMeeting error: ${error}`);
	}

	return [];
};

export const CreateOnsiteLocation = async (
	token: string,
	meetingId: number,
	locationName: string | null,
	safetyImage: string | null,
	holdSlide: string | null,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					locationName,
					safetyImage,
					holdSlide,
				}),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`CreateOnsiteLocation error: ${response.status}`);
	} catch (error) {
		logger.error(`CreateOnsiteLocation error: ${error}`);
	}

	return null;
};

export const UpdateOnsiteLocation = async (
	token: string,
	meetingId: number,
	locationId: number,
	updatedProps: Partial<OnsiteLocation>,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations/${locationId}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify(updatedProps),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`UpdateOnsiteLocation error: ${response.status}`);
	} catch (error) {
		logger.error(`UpdateOnsiteLocation error: ${error}`);
	}

	return null;
};

export const DeleteOnsiteLocation = async (
	token: string,
	meetingId: number,
	locationId: number,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations/${locationId}`),
			{
				method: HttpRequest.Delete,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.json();
		}

		logger.error(`DeleteOnsiteLocation error: ${response.status}`);
	} catch (error) {
		logger.error(`DeleteOnsiteLocation error: ${error}`);
	}

	return null;
};

export const GetPreviousLocationsForMeeting = async (
	token: string,
	meetingId: number,
): Promise<PreviousLocation[]> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v3/greenroom/${meetingId}/previous-onsite-locations`,
			{
				method: HttpRequest.Get,
				...getAuthenticatedRequestInit(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: [];
		}

		logger.error(`GetPreviousLocationsForMeeting error: ${response.status}`);
	} catch (error) {
		logger.error(`GetPreviousLocationsForMeeting error: ${error}`);
	}

	return [];
};

interface VerifyOnsiteLocationResult {
	id: number;
	meeting: number;
	region?: string;
}

export const VerifyVenueConnection = async (
	token: string,
	meetingSlug: string,
): Promise<VerifyOnsiteLocationResult | null> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v3/greenroom/${meetingSlug}/verify-onsite-location`,
			{
				method: HttpRequest.Get,
				...getAuthenticatedRequestInit(token),
			},
		);

		if (response.ok && response.status === 200) {
			return await response.json();
		}

		logger.error(`VerifyVenueConnection error: ${response.status}`);
	} catch (error) {
		logger.error(`VerifyVenueConnection error: ${error}`);
	}

	return null;
};

export const VerifyOnsiteLocationForMeeting = async (
	token: string,
	meetingSlug: string,
	pathName: string,
): Promise<VerifyOnsiteLocationResult | null> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v3/greenroom/${meetingSlug}/verify-onsite-location/${pathName}`,
			{
				method: HttpRequest.Get,
				...getAuthenticatedRequestInit(token),
			},
		);

		if (response.ok && response.status === 200) {
			const result = await response.json();
			if (result.id) {
				return result;
			}
		}

		logger.error(`VerifyOnsiteLocationForMeeting error: ${response.status}`);
	} catch (error) {
		logger.error(`VerifyOnsiteLocationForMeeting error: ${error}`);
	}

	return null;
};

export const SetMediaRegionForRoom = async (
	token: string,
	roomId: number,
	region: string,
): Promise<boolean> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_SYNC_HOST}/v1/room/${roomId}/set-media-region`,
			{
				method: HttpRequest.Put,
				headers: {
					'Content-Type': 'application/json',
					'bl-token': token,
				},
				body: JSON.stringify({ region }),
			},
		);

		return response.ok;
	} catch (error) {
		logger.error(`SetMediaRegionForRoom error: ${error}`);
		return false;
	}
};
